import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { 
  Brain, ArrowLeft, Trophy, Target, Zap, Clock, 
  CheckCircle2, XCircle, Sparkles, RotateCcw,
  Folder, Play, Star, BarChart3
} from 'lucide-react';
import { useAuthStore } from '../lib/auth';
import { dailyCategoriesAPI } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png";

const PHASES = {
  SETUP: 'setup',
  LOADING: 'loading',
  PLAYING: 'playing',
  RESULTS: 'results'
};

const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: 'Easy', description: '$200-$400 clues', color: 'text-green-400' },
  { value: 'medium', label: 'Medium', description: '$400-$800 clues', color: 'text-yellow-400' },
  { value: 'hard', label: 'Hard', description: '$800+ clues', color: 'text-red-400' },
  { value: 'mixed', label: 'Mixed', description: 'All difficulty levels', color: 'text-purple-400' },
];

const CLUE_COUNT_OPTIONS = [5, 10, 15, 25];

const LOADING_MESSAGES = [
  { text: "Loading category clues...", icon: Folder },
  { text: "Crafting unique questions...", icon: Sparkles },
  { text: "Preparing your training...", icon: Target },
  { text: "Almost ready...", icon: Zap },
];

export default function DailyCategories() {
  useSEO('Category Training | JepPrep', 'Train in specific trivia categories to strengthen your weakest areas.');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [phase, setPhase] = useState(PHASES.SETUP);
  const [todaysCategories, setTodaysCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [difficulty, setDifficulty] = useState('mixed');
  const [numClues, setNumClues] = useState(10);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  // Loading state
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  
  // Game state
  const [sessionId, setSessionId] = useState(null);
  const [currentClue, setCurrentClue] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalClues, setTotalClues] = useState(0);
  const [answer, setAnswer] = useState('');
  const [timeLeft, setTimeLeft] = useState(20);
  const [isAnswering, setIsAnswering] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [results, setResults] = useState(null);
  
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch today's categories on mount
  useEffect(() => {
    fetchTodaysCategories();
  }, []);

  const fetchTodaysCategories = async () => {
    try {
      const response = await dailyCategoriesAPI.getTodaysCategories();
      setTodaysCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Timer effect
  useEffect(() => {
    if (phase === PHASES.PLAYING && timeLeft > 0 && !feedback) {
      timerRef.current = setTimeout(() => {
        setTimeLeft(t => t - 1);
      }, 1000);
    } else if (phase === PHASES.PLAYING && timeLeft === 0 && !feedback) {
      handleSubmitAnswer(true);
    }
    
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [phase, timeLeft, feedback]);

  // Focus input when playing
  useEffect(() => {
    if (phase === PHASES.PLAYING && !feedback && inputRef.current) {
      inputRef.current.focus();
    }
  }, [phase, feedback, currentIndex]);

  const startGame = async () => {
    if (!selectedCategory) return;
    
    setPhase(PHASES.LOADING);
    setLoadingProgress(0);
    setLoadingMessage(LOADING_MESSAGES[0]);
    
    // Animate progress
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => Math.min(prev + 10, 90));
    }, 400);
    
    const messageInterval = setInterval(() => {
      setLoadingMessage(prev => {
        const idx = LOADING_MESSAGES.findIndex(m => m.text === prev.text);
        return LOADING_MESSAGES[(idx + 1) % LOADING_MESSAGES.length];
      });
    }, 1000);
    
    try {
      const response = await dailyCategoriesAPI.startSession({
        category: selectedCategory,
        difficulty,
        num_clues: numClues
      });
      
      clearInterval(progressInterval);
      clearInterval(messageInterval);
      setLoadingProgress(100);
      
      setTimeout(() => {
        setSessionId(response.data.session_id);
        setCurrentClue(response.data.current_clue);
        setCurrentIndex(0);
        setTotalClues(response.data.total_clues);
        setTimeLeft(20);
        setAnswer('');
        setFeedback(null);
        setPhase(PHASES.PLAYING);
      }, 300);
    } catch (error) {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
      console.error('Error starting game:', error);
      alert('Failed to start training session. Please try again.');
      setPhase(PHASES.SETUP);
    }
  };

  const handleSubmitAnswer = async (timeout = false) => {
    if (isAnswering) return;
    setIsAnswering(true);
    
    const timeSpent = (20 - timeLeft) * 1000;
    
    try {
      const response = await dailyCategoriesAPI.submitAnswer(sessionId, {
        answer: timeout ? '' : answer,
        question_index: currentIndex,
        time_taken_ms: timeSpent
      });
      
      setFeedback({
        isCorrect: response.data.is_correct,
        correctAnswer: response.data.correct_answer,
        timeout
      });
      
      // Wait 2 seconds then move to next clue or results
      setTimeout(async () => {
        if (response.data.complete) {
          // Fetch full results
          const resultsResponse = await dailyCategoriesAPI.getResults(sessionId);
          setResults(resultsResponse.data);
          setPhase(PHASES.RESULTS);
        } else {
          setCurrentClue(response.data.next_clue);
          setCurrentIndex(response.data.next_clue.index);
          setTimeLeft(20);
          setAnswer('');
          setFeedback(null);
        }
        setIsAnswering(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting answer:', error);
      setIsAnswering(false);
    }
  };

  const resetGame = () => {
    setPhase(PHASES.SETUP);
    setSelectedCategory(null);
    setSessionId(null);
    setCurrentClue(null);
    setResults(null);
    setFeedback(null);
    fetchTodaysCategories();
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-gray-300">
                  <ArrowLeft className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Dashboard</span>
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12 shrink-0" />
            </div>
            {phase === PHASES.PLAYING && (
              <div className="flex items-center gap-4">
                <Badge variant="outline" className="text-[hsl(var(--brand-gold-500))] border-[hsl(var(--brand-gold-500))]">
                  {currentIndex + 1} / {totalClues}
                </Badge>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          {/* SETUP PHASE */}
          {phase === PHASES.SETUP && (
            <motion.div
              key="setup"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Header */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4">
                  <Folder className="w-8 h-8 text-white" />
                </div>
                <h1 className="font-display font-bold text-3xl text-white mb-2">Daily Categories</h1>
                <p className="text-gray-400">Choose from today's featured categories and train at your pace</p>
              </div>

              {/* Today's Categories */}
              <div>
                <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                  Today's Featured Categories
                </h2>
                
                {loadingCategories ? (
                  <div className="text-center py-8 text-gray-400">Loading categories...</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {todaysCategories.map((cat, idx) => (
                      <Card 
                        key={cat.name}
                        className={`cursor-pointer transition-all ${
                          selectedCategory === cat.name 
                            ? 'bg-[hsl(var(--brand-gold-500))]/20 border-[hsl(var(--brand-gold-500))]' 
                            : 'bg-[hsl(var(--secondary))] border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/50'
                        }`}
                        onClick={() => setSelectedCategory(cat.name)}
                        data-testid={`category-card-${idx}`}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between mb-3">
                            <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30">
                              #{idx + 1}
                            </Badge>
                            {selectedCategory === cat.name && (
                              <CheckCircle2 className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                            )}
                          </div>
                          <h3 className="text-white font-bold text-lg mb-2">{cat.name}</h3>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-gray-400">
                              <Play className="w-4 h-4 inline mr-1" />
                              {cat.plays_today} plays
                            </span>
                            {cat.best_accuracy_today > 0 && (
                              <span className="text-green-400">
                                Best: {cat.best_accuracy_today}%
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Configuration */}
              {selectedCategory && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-6"
                >
                  {/* Difficulty Selection */}
                  <div>
                    <h3 className="text-white font-semibold mb-3">Difficulty</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {DIFFICULTY_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setDifficulty(opt.value)}
                          className={`p-3 rounded-lg border transition-all ${
                            difficulty === opt.value
                              ? 'bg-[hsl(var(--brand-gold-500))]/20 border-[hsl(var(--brand-gold-500))]'
                              : 'bg-[hsl(var(--secondary))] border-[hsl(var(--border))] hover:border-gray-600'
                          }`}
                          data-testid={`difficulty-${opt.value}`}
                        >
                          <p className={`font-medium ${difficulty === opt.value ? 'text-[hsl(var(--brand-gold-500))]' : opt.color}`}>
                            {opt.label}
                          </p>
                          <p className="text-xs text-gray-500">{opt.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Number of Clues */}
                  <div>
                    <h3 className="text-white font-semibold mb-3">Number of Clues</h3>
                    <div className="flex gap-3">
                      {CLUE_COUNT_OPTIONS.map((num) => (
                        <button
                          key={num}
                          onClick={() => setNumClues(num)}
                          className={`flex-1 py-3 rounded-lg border transition-all ${
                            numClues === num
                              ? 'bg-[hsl(var(--brand-gold-500))] border-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-bold'
                              : 'bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-white hover:border-gray-600'
                          }`}
                          data-testid={`clue-count-${num}`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Start Button */}
                  <Button
                    onClick={startGame}
                    className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg"
                    data-testid="start-training-btn"
                  >
                    <Play className="w-5 h-5 mr-2" />
                    Start Training
                  </Button>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* LOADING PHASE */}
          {phase === PHASES.LOADING && (
            <motion.div
              key="loading"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="flex justify-center"
            >
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] max-w-md w-full">
                <CardContent className="p-8">
                  <div className="text-center space-y-6">
                    <motion.div 
                      className="w-20 h-20 mx-auto bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center"
                      animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      {loadingMessage.icon && <loadingMessage.icon className="w-10 h-10 text-white" />}
                    </motion.div>
                    
                    <div>
                      <h2 className="text-xl font-display text-white mb-2">Preparing Your Training</h2>
                      <motion.p 
                        key={loadingMessage.text}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-gray-400"
                      >
                        {loadingMessage.text}
                      </motion.p>
                    </div>
                    
                    <div className="space-y-2">
                      <Progress value={loadingProgress} className="h-2 bg-[hsl(var(--accent))]" />
                      <p className="text-xs text-gray-500">
                        {loadingProgress < 100 ? `Loading ${selectedCategory}...` : 'Ready!'}
                      </p>
                    </div>
                    
                    <div className="bg-[hsl(var(--accent))] rounded-lg p-4 text-left">
                      <p className="text-xs text-gray-400 mb-1">Training: {selectedCategory}</p>
                      <p className="text-sm text-gray-300">
                        {numClues} clues | {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)} difficulty | 20 sec timer
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* PLAYING PHASE */}
          {phase === PHASES.PLAYING && (
            <motion.div
              key="playing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full max-w-2xl mx-auto"
            >
              {/* Progress & Timer */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span>Question {currentIndex + 1} of {totalClues}</span>
                  <span className="font-mono text-white">{timeLeft}s</span>
                </div>
                <div className="timer-bar">
                  <div 
                    className={`timer-bar-fill ${timeLeft <= 2 ? 'danger' : timeLeft <= 5 ? 'warning' : ''}`}
                    style={{ width: `${(timeLeft / 15) * 100}%` }}
                  />
                </div>
              </div>

              {/* Question/Result Card */}
              <AnimatePresence mode="wait">
                {feedback ? (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="text-center"
                  >
                    <Card className={`border-2 ${feedback.isCorrect ? 'bg-green-500/10 border-green-500' : feedback.timeout ? 'bg-orange-500/10 border-orange-500' : 'bg-red-500/10 border-red-500'}`}>
                      <CardContent className="p-8">
                        <div className="mb-4">
                          {feedback.isCorrect ? (
                            <CheckCircle2 className="w-16 h-16 mx-auto text-green-500" />
                          ) : feedback.timeout ? (
                            <Clock className="w-16 h-16 mx-auto text-orange-500" />
                          ) : (
                            <XCircle className="w-16 h-16 mx-auto text-red-500" />
                          )}
                        </div>
                        <h2 className={`text-2xl font-display mb-4 ${feedback.isCorrect ? 'text-green-500' : feedback.timeout ? 'text-orange-500' : 'text-red-500'}`}>
                          {feedback.isCorrect ? 'Correct!' : feedback.timeout ? "Time's Up!" : 'Incorrect'}
                        </h2>
                        <p className="text-gray-300">Correct answer:</p>
                        <p className="text-white text-xl font-semibold">{feedback.correctAnswer}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ) : currentClue ? (
                  <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <Card className="clue-display mb-6">
                      <div className="text-[hsl(var(--brand-gold-500))] text-sm font-semibold mb-4">
                        {currentClue?.category || selectedCategory}
                      </div>
                      <div className="text-white text-xl md:text-2xl" data-testid="clue-text">
                        {currentClue?.question || 'Loading clue...'}
                      </div>
                    </Card>

                    <form onSubmit={(e) => { e.preventDefault(); if (answer.trim() && !isAnswering) handleSubmitAnswer(); }}>
                      <div className="flex gap-3">
                        <Input
                          ref={inputRef}
                          type="text"
                          placeholder="Type your answer..."
                          value={answer}
                          onChange={(e) => setAnswer(e.target.value)}
                          autoFocus
                          disabled={isAnswering}
                          className="flex-1 bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-lg py-6"
                          data-testid="answer-input"
                        />
                        <Button
                          type="submit"
                          disabled={isAnswering || !answer.trim()}
                          className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-8 py-6"
                          data-testid="submit-answer-btn"
                        >
                          Submit
                        </Button>
                      </div>
                    </form>
                  </motion.div>
                ) : (
                  <div className="text-center text-gray-400">Loading...</div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* RESULTS PHASE */}
          {phase === PHASES.RESULTS && results && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Score Card */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardContent className="p-4 sm:p-8 text-center">
                  <Trophy className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-[hsl(var(--brand-gold-500))] mb-3 sm:mb-4" />
                  <h1 className="font-display font-bold text-2xl sm:text-3xl text-white mb-2">Training Complete!</h1>
                  <p className="text-gray-400 text-sm sm:text-base mb-4">{results.category} - {results.difficulty}</p>
                  
                  <div className="text-4xl sm:text-6xl font-mono font-bold text-[hsl(var(--brand-gold-500))] mb-4" data-testid="final-accuracy">
                    {results.accuracy}%
                  </div>
                  
                  <div className="flex justify-center gap-4 sm:gap-8 text-sm sm:text-lg mb-6 sm:mb-8">
                    <div className="text-green-500">
                      <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 inline mr-1 sm:mr-2" />
                      {results.correct_count} Correct
                    </div>
                    <div className="text-red-500">
                      <XCircle className="w-5 h-5 sm:w-6 sm:h-6 inline mr-1 sm:mr-2" />
                      {results.total - results.correct_count} Incorrect
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                    <Button 
                      onClick={resetGame}
                      className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-6 py-5 w-full sm:w-auto"
                      data-testid="play-again-btn"
                    >
                      <RotateCcw className="w-5 h-5 mr-2" />
                      Train Again
                    </Button>
                    <Button 
                      onClick={() => navigate('/daily-categories/stats')}
                      variant="outline"
                      className="border-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white hover:border-[hsl(var(--brand-blue-800))] px-6 py-5 w-full sm:w-auto"
                      data-testid="view-stats-btn"
                    >
                      <BarChart3 className="w-5 h-5 mr-2" />
                      View Stats
                    </Button>
                    <Button 
                      onClick={() => navigate('/dashboard')}
                      variant="outline"
                      className="border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--accent))] px-6 py-5 w-full sm:w-auto"
                    >
                      <ArrowLeft className="w-5 h-5 mr-2" />
                      Dashboard
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Question Review */}
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardContent className="p-6">
                  <h3 className="text-white font-bold text-lg mb-4">Question Review</h3>
                  <div className="space-y-3">
                    {results.questions?.map((q, idx) => (
                      <div 
                        key={q.question || `q-${idx}`} 
                        className={`p-4 rounded-lg border ${q.is_correct ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}
                      >
                        <div className="flex items-start gap-3">
                          {q.is_correct ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <p className="text-white mb-2">{q.question}</p>
                            <div className="text-sm">
                              <p className="text-green-400">Correct: {q.correct_answer}</p>
                              {!q.is_correct && q.player_answer && (
                                <p className="text-red-400">Your answer: {q.player_answer}</p>
                              )}
                              {!q.is_correct && !q.player_answer && (
                                <p className="text-gray-500">No answer (timeout)</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
